import { breakText } from 'jointjs/src/util/util.mjs';
import { cls_autoplaceholder } from '~/components/Editor2/plugins/autoplaceholder/constants';
import types from '~/modules/customFieldTypes';
import { NODE_TYPES } from '~/modules/nodeTypes';
import { checkFeatureFlag } from '~/modules/helpers';
import { RE } from '~/modules/helpers/regexp';
import { calculatePortsLayout } from './calculatePortsLayout';

//sst: replace paragraph tag and NonBreakingSpace entity with space e.g.: for better view of title
const paragraphMarkerOrNonBreakingSpaceEntityRE = /<\/?p[^>]*>|&nbsp;/g;
const replaceParagraphMarkerAndNonBreakingSpaceEntityWithSingleSpace = str =>
    str ? str.replace(paragraphMarkerOrNonBreakingSpaceEntityRE, '\u0020') : '';

/**
 * Convert field value in according to server needs
 * @param field
 * @returns {string | null | number}
 * @private
 * @author sst
 */
export function convertFieldValue(field) {
    if (!field.value) return field;
    if (field.type === types.DATE || field.type === types.TIME)
        field.value.isValid ? field.value.toISOString() : field.value; // ? <-
    if (field.type === types.LIST) {
        //sst: reset name and value if value is empty ([]) because select component return empty array when clear selection
        if (!field.value.length) {
            field.name = '';
            field.value = '';
        }
    }
    return field;
}

/**
 * Combine graph node and values (configuration)
 * @param {string} type - node type
 * @param {*} node - joint element
 * @param {object} data - values
 * @param mongoId
 * @author sst
 */
export function setNodeValues(type, node, data, mongoId = null) {
    //sst: common node data
    if (mongoId) node.set('a_id', mongoId);

    switch (type) {
        case NODE_TYPES.ACTION:
            //sst graph view
            node.attr('.rotatable/data-type', data.actionType.toLowerCase());
            node.attr('.title/text', data.actionType);
            node.attr('.description/text', formatNodeTitle(data.actionTitle));

            //sst: info icon (remove if not necessary)
            if (data.action) node.attr('.info/text', '');

            //sst internal data
            node.set('actionName', data.action);
            node.set(
                'cartesian_product',
                data.hasOwnProperty('cartesian_product')
                    ? data.cartesian_product
                    : checkFeatureFlag('cartesian_product_default_value')
            );
            node.set('breakPoint', data.breakPoint ? data.breakPoint : false);
            node.set(
                'authorizer',
                data.authorizer ? data.authorizer.value || data.authorizer : ''
            );
            node.set('actionNameDisplay', data.actionNameDisplay);
            node.set('actionType', data.actionType);
            node.set('actAsset', data.resource);

            //sst: details data
            node.set(
                'actionTitle',
                replaceParagraphMarkerAndNonBreakingSpaceEntityWithSingleSpace(
                    data.actionTitle
                )
            );
            node.set('actionDesc', ''); //sst: set empty for action
            node.set('actToDo', data.action + '_1');
            node.set('integrationTag', data.integrationTag);
            node.set('getDefaultResource', data.getDefaultResource);

            let required_fields = data.configuration.required_fields.map(
                field => {
                    return convertFieldValue(field);
                }
            );

            let opt_fields = data.configuration.opt_fields.map(field => {
                return convertFieldValue(field);
            });

            node.set('actConf', {
                required_fields: required_fields,
                opt_fields: opt_fields
            });

            break;
        case NODE_TYPES.TASK.toLowerCase():
            //sst: graph view
            node.attr('.rotatable/data-type', 'task');
            node.attr('.title/text', 'Task');
            node.attr('.description/text', formatNodeTitle(data.title));
            node.attr('.info/text', ''); //remove info icon

            //sst internal data
            node.set('actionType', 'Task');
            node.set('actionName', 'task');
            node.set('desc', data.desc);
            node.set('auth', data.auth ? data.auth.value || data.auth : '');

            node.set(
                'title',
                replaceParagraphMarkerAndNonBreakingSpaceEntityWithSingleSpace(
                    data.title
                )
            );

            node.set('owner', data.owner ? data.owner.value || data.owner : '');
            node.set('dueDate', data.dueDate);
            let mandatory = false;
            if (data.mandatory) {
                mandatory = data.mandatory.value || data.mandatory;
            }
            node.set('mandatory', mandatory);
            node.set('is_automatic', 0);

            //sst: create customFields object
            let customFields = {};
            let fieldToSkip = [
                'title',
                'desc',
                'auth',
                'owner',
                'dueDate',
                'mandatory',
                'stepType'
            ];
            for (let value in data) {
                if (fieldToSkip.includes(value)) {
                    delete data[value];
                } else {
                    customFields[value] = data[value];
                }
            }
            node.set('customFields', customFields);
            break;
        case NODE_TYPES.CONDITION:
        case NODE_TYPES.FILTER:
            let key = 'conditions';

            if (type === NODE_TYPES.CONDITION) {
                key = 'multiple_conditions';

                //sst: get ports
                let ports = data.conditions.map(condition => condition.port);

                //sst: add failure port
                ports.push('failure');

                //sst: set ports to node
                node.set('outPorts', ports);

                //sst: calculate port layout and replace attrs
                const portLayout = calculatePortsLayout(
                    'condition',
                    data.conditions
                );
                node.attributes.ports.groups.out.position.args.startAngle =
                    portLayout.angle;
                node.attributes.ports.groups.out.position.args.step =
                    portLayout.step;
            }

            if (type === NODE_TYPES.FILTER) {
                node.set(
                    'cartesian_product',
                    data.hasOwnProperty('cartesian_product')
                        ? data.cartesian_product
                        : checkFeatureFlag('cartesian_product_default_value')
                );
                node.set('split_by', data.splitBy || '');
            }

            node.set(key, data.conditions);
            break;
        case NODE_TYPES.START:
            node.set('inputParams', data.inputParams);
            node.set('inputPayload', data.inputPayload);
            node.set('selectedPayloadType', data.selectedPayloadType);
            break;
        case NODE_TYPES.END:
            node.set('outputParams', data.outputParams);
            break;
        case NODE_TYPES.USERCHOICE:
            node.set(
                'title',
                replaceParagraphMarkerAndNonBreakingSpaceEntityWithSingleSpace(
                    data.title
                )
            );
            let answers = data.answers
                ? data.answers.map(answer => {
                      return answer.value;
                  })
                : ['success', 'failure'];
            node.set('question', data.question);
            if (Array.isArray(data.authorizer)) {
                const authorizers = data.authorizer.map(auth => auth.value);
                node.set('authorizer', authorizers);
            } else {
                node.set(
                    'authorizer',
                    data.authorizer?.value ?? data.authorizer?.value
                        ? data.authorizer.value
                        : data.authorizer
                        ? data.authorizer
                        : ''
                );
            }

            //sst: send mail only if there is an authorizer
            let sendMailVal = false;
            if (
                (Array.isArray(data.authorizer) &&
                    data.authorizer.length === 1 &&
                    data.authorizer[0].value) ||
                RE.PLAYBOOK.placeholder.flowOrPlaybookPayload.test(
                    data.authorizer
                )
            ) {
                sendMailVal = data.sendMail;
            } else {
                sendMailVal = data.authorizer?.value ? data.sendMail : false;
            }

            node.set('sendMail', sendMailVal);
            // set two-step mail value only if sendMail is true.
            node.set(
                'sendTwoStepMail',
                sendMailVal ? data.sendTwoStepMail : false
            );
            node.set('sendSlackMsg', data.sendSlackMsg);
            node.set('sendShortSlackMsg', data.sendShortSlackMsg);

            //sst: forced to true until the backward compatibility with no login answer (prev value: data.needLogin)
            node.set('needLogin', true);

            node.set('outPorts', answers);
            node.set('answers', answers);
            node.set('externalUserEmail', data.externalUserEmail);

            //sst: calculate expires value
            let expiresValue = 0;

            switch (data.expiresUnit?.value) {
                case 'minutes':
                    expiresValue = data.expiresValue * 60;
                    break;
                case 'hours':
                    expiresValue = data.expiresValue * 3600;
                    break;
                case 'days':
                    expiresValue = data.expiresValue * 86400;
                    break;
                case 'weeks':
                    expiresValue = data.expiresValue * 604800;
                    break;
            }

            node.set('expires', data.expires);
            node.set('expiresValue', expiresValue);
            node.set('expiresValueRaw', data.expiresValue);
            node.set('expiresUnit', data.expiresUnit?.value);
            node.set('expiresDefault', data.expiresDefault?.value);

            //sst: update port layout by answers
            const answersCount = answers.length;
            let startAngle, step;
            if (answersCount === 1) {
                startAngle = 90;
                step = 0;
            } else if (answersCount < 7) {
                startAngle = 0;
                step = 180 / (answersCount - 1);
            } else {
                startAngle = -45;
                step = 225 / (answersCount - 2);
            }
            node.attributes.ports.groups.out.position.args.step = step;
            node.attributes.ports.groups.out.position.args.startAngle =
                startAngle;

            //sst: add/update question tooltip
            node.attr(
                '.user-choice-icon/data-tooltip',
                data.question.replace(/(\r\n|\n|\r)/gm, ' ')
            );
            node.attr(
                '.title/text',
                formatNodeTitle(data.title) || 'USER CHOICE'
            );
            break;
        case NODE_TYPES.NESTED:
        case NODE_TYPES.RUNBOOK:
            let name = data.runbook?.label;

            node.set('runbook_id', data.runbook?.value || data.runbook);
            node.set('name', name || '');
            node.set('inputs', data.inputs);
            node.attr(
                '.name/text',
                name ? formatNodeTitle(name) : 'Playbook not specified'
            );
            break;
    }
    return node;
}

/**
 * Format node title with or without placeholder
 * @param {string} name
 * @param {Object} options
 * @returns {string}
 * @author sst
 */
const newLinesRE = /\r\n|\r|\n/;

export function formatNodeTitle(
    name,
    options = { plainText: false, size: 160 }
) {
    // console.count(name);
    let str = name || '';
    if (str) {
        str = str.trim();

        //FM: create container and add title
        const container = document.createElement('div');
        container.innerHTML = str;

        //FM: get placeholders
        const placeholders =
            container.getElementsByClassName(cls_autoplaceholder);

        /**-/
         console.debug({
            cls: cls_autoplaceholder,
            markup: container.outerHTML,
            founded: placeholders.length || 'no'
        });
         /**/

        if (placeholders.length) {
            //FM: get text from html title
            let result = container.textContent;
            //FM: replace placeholder with resolved string
            for (const placeholder of placeholders) {
                const value = placeholder.textContent;
                const resolved = `[${value}]`;
                // console.log({ placeholder, value, resolved });
                result = result.replace(value, resolved);
            }
            str = result;
        }

        str =
            replaceParagraphMarkerAndNonBreakingSpaceEntityWithSingleSpace(str);
        // console.log('clean:', str);
        if (!options.plainText) {
            //sst: formatting for canvas:
            str = breakText(str, { width: options.size })
                .split(newLinesRE)
                .slice(0, 4) // ( get first 4 lines of title )
                .join('\r\n');
            //sst: if title length is greater of 100 character add ellipsis
            if (99 < str.length) {
                str = `${str}\u2026`; // str&hellip;
            }
        }
        // console.trace('formatted', str);
    }
    return str;
}
